import React, { useState, useEffect, useCallback, useRef } from 'react';
import type { AxiosError } from 'axios';
import {
  Box,
  Container,
  Typography,
  Paper,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import SharedSidebar from '../../components/layout/SharedSidebar';
import CenterWorkspace from '../../components/layout/CenterWorkspace';
import ConfigurationPanel from './components/ConfigurationPanel';
import ScannerStatus from './components/ScannerStatus';
import FolderAssignmentsView from './components/FolderAssignmentsView';
import UserBrowser, { User as BrowserUser } from '../../components/shared/UserBrowser';
import GenerationProgressView from './components/GenerationProgressView';
import { batchProfileApi } from './services/batchProfileApi';
import {
  ScanConfiguration,
  ScanResult,
  UserMatch,
  BatchGenerationOptions,
  BatchGenerationProgress,
  BatchGenerationResult,
  BatchGenerationStartRequest,
  FolderAssignment,
  AssignedUserSummary,
  SharePointFolder,
  GenerationMatchPayload
} from './types';

const mapGraphUserToUser = (entry: any): BrowserUser => ({
  id: entry?.id ?? '',
  displayName: entry?.displayName ?? entry?.userPrincipalName ?? '',
  mail: entry?.mail ?? entry?.userPrincipalName ?? '',
  jobTitle: entry?.jobTitle,
  department: entry?.department,
  employmentType: entry?.employmentType,
  hasProfile: entry?.has_profile ?? entry?.hasProfile ?? false,
  profileLastUpdated: entry?.profileLastUpdated ?? entry?.updatedAt
});

const mapMatchFromApi = (match: any): UserMatch => {
  const normalizedUser = match?.user ? mapGraphUserToUser(match.user) : null;

  const folderFromApi = match?.folder ?? {};
  const folder: SharePointFolder = {
    id:
      folderFromApi.id ??
      folderFromApi.itemId ??
      match?.itemId ??
      match?.folderId ??
      match?.id ??
      '',
    name: folderFromApi.name ?? match?.folderName ?? '',
    path: folderFromApi.path ?? match?.folderPath ?? '',
    webUrl:
      folderFromApi.webUrl ??
      folderFromApi.web_url ??
      match?.folderUrl ??
      match?.folder_url ??
      '',
    consultantType:
      folderFromApi.consultantType ??
      folderFromApi.consultant_type ??
      match?.consultantType,
    department:
      folderFromApi.department ??
      folderFromApi.departmentPath ??
      match?.userDepartment ??
      match?.folder?.department,
    siteId: folderFromApi.siteId ?? folderFromApi.site_id ?? match?.siteId ?? match?.site_id,
    siteKey: folderFromApi.siteKey ?? folderFromApi.site_key ?? match?.siteKey ?? match?.site_key,
    driveId: folderFromApi.driveId ?? folderFromApi.drive_id ?? match?.driveId ?? match?.drive_id,
    hasDocuments:
      folderFromApi.hasDocuments ??
      (typeof match?.hasDocuments === 'boolean' ? match.hasDocuments : Boolean(match?.documentCount)),
    documentCount: folderFromApi.documentCount ?? match?.documentCount,
    lastModified: folderFromApi.lastModified ?? match?.lastModified,
  metadata: folderFromApi.metadata
  } as SharePointFolder;

  return {
    id: match?.id ?? `match_${folder.id}`,
    user: normalizedUser,
    folder,
    userName: match?.userName ?? normalizedUser?.displayName ?? '',
    userEmail: match?.userEmail ?? normalizedUser?.mail ?? '',
    userDepartment: match?.userDepartment ?? normalizedUser?.department,
    folderName: match?.folderName ?? folder.name ?? '',
    folderPath: match?.folderPath ?? folder.path ?? '',
    hasDocuments: Boolean(match?.hasDocuments ?? folder.hasDocuments ?? false),
    documentCount: match?.documentCount ?? folder.documentCount,
    confidence: match?.confidence ?? 0,
    matchMethod: match?.matchMethod ?? match?.match_type ?? 'none',
    isApproved: match?.isApproved ?? false,
    isRejected: match?.isRejected ?? false,
    manuallyVerified: match?.manuallyVerified ?? false,
    requiresReview:
      match?.requiresReview ??
      (typeof match?.confidence === 'number' ? match.confidence < 0.8 : true),
    alternativeUsers: (match?.alternativeUsers as BrowserUser[] | undefined) ?? [],
    notes: match?.notes ?? match?.details,
    updatedAt: match?.updatedAt
  };
};

const computeAssignmentStats = (entries: FolderAssignment[]) => {
  const assigned = entries.filter((entry) => entry.isAssigned).length;
  return {
    total: entries.length,
    assigned,
    unassigned: entries.length - assigned
  };
};

const buildEmptyAssignment = (folder: SharePointFolder): FolderAssignment => ({
  folder,
  isAssigned: false
});

const BatchProfiles: React.FC = () => {
  const { getAccessToken } = useAuth();

  // State management
  const [activeStep, setActiveStep] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  // Step 1: SharePoint Scanning
  const [scanConfig, setScanConfig] = useState<ScanConfiguration>({
    siteKey: 'hr',
    driveId: '',
    targetLibrary: 'Professional Profile Folders',
    scanDepth: 3,
    excludePatterns: ['_*', 'Archive', 'CVs', 'Bios', 'Templates'],
    followLinks: false
  });
  const [isScanning, setIsScanning] = useState(false);
  const [scanProgress, setScanProgress] = useState(0);
  const [scanResults, setScanResults] = useState<ScanResult | null>(null);
  const [scanSessionId, setScanSessionId] = useState<string | null>(null);
  const [scanCacheKey, setScanCacheKey] = useState<string | null>(null);
  const [currentScanPath, setCurrentScanPath] = useState<string>('');

  // Step 2: User Matching
  const [matches, setMatches] = useState<UserMatch[]>([]);
  const [isMatching, setIsMatching] = useState(false);
  const [isPersistingMatch, setIsPersistingMatch] = useState(false);

  // Folder Assignments
  const [assignments, setAssignments] = useState<FolderAssignment[]>([]);
  const [assignmentStats, setAssignmentStats] = useState({ total: 0, assigned: 0, unassigned: 0 });
  const [isLoadingAssignments, setIsLoadingAssignments] = useState(false);
  const [assignmentError, setAssignmentError] = useState<string | null>(null);
  const [assignmentDialog, setAssignmentDialog] = useState<{
    open: boolean;
    folder: SharePointFolder | null;
    matchId?: string | null;
    currentUser?: AssignedUserSummary | null;
  }>({ open: false, folder: null, matchId: null, currentUser: null });
  const [isBulkAssigning, setIsBulkAssigning] = useState(false);
  const [isSubmittingAssignment, setIsSubmittingAssignment] = useState(false);

  // Step 3: Batch Generation
  const [generationOptions, setGenerationOptions] = useState<BatchGenerationOptions>({
    skipExisting: true,
    retryAttempts: 2,
    batchSize: 5,
    testMode: false
  });
  const [isGenerating, setIsGenerating] = useState(false);
  const [retryingUsers, setRetryingUsers] = useState<string[]>([]);
  const [isPlaceholderScanRunning, setIsPlaceholderScanRunning] = useState(false);
  const [generationProgress, setGenerationProgress] = useState<BatchGenerationProgress | null>(null);
  const [generationResult, setGenerationResult] = useState<BatchGenerationResult | null>(null);

  const matchesRef = useRef<UserMatch[]>([]);
  useEffect(() => {
    matchesRef.current = matches;
  }, [matches]);

  const buildGenerationStartPayload = useCallback((): BatchGenerationStartRequest => {
    const payloadMatches: GenerationMatchPayload[] = assignments
      .filter((assignment) => assignment.isAssigned && assignment.assignedUser)
      .map((assignment) => {
        const assignedUser = assignment.assignedUser;
        const folder = assignment.folder;

        const driveId = folder.driveId ?? folder.drive_id ?? '';
        const rawPath = folder.path ?? '';
        const path = rawPath.replace(/^\/+/, '');

        if (!assignedUser?.userId || !driveId || !path) {
          return null;
        }

        const matchPayload: GenerationMatchPayload = {
          match_id: assignment.matchId ?? undefined,
          user_id: assignedUser.userId,
          user_display_name: assignedUser.displayName ?? undefined,
          user_email: assignedUser.email ?? undefined,
          folder: {
            site_id: folder.siteId ?? folder.site_id ?? undefined,
            site_key: folder.siteKey ?? folder.site_key ?? undefined,
            drive_id: driveId,
            path,
            name: folder.name ?? undefined,
            web_url: folder.webUrl ?? folder.web_url ?? undefined
          }
        };

        return matchPayload;
      })
      .filter((match): match is GenerationMatchPayload => Boolean(match));

    return {
      matches: payloadMatches,
      options: {
        skip_existing: generationOptions.skipExisting,
        retry_attempts: generationOptions.retryAttempts,
        batch_size: generationOptions.batchSize,
        test_mode: generationOptions.testMode
      }
    };
  }, [assignments, generationOptions]);

  const fetchGeneratedProfile = useCallback(async (userId: string): Promise<string> => {
    const token = await getAccessToken();
    return batchProfileApi.getGeneratedProfileMarkdown(userId, token || '');
  }, [getAccessToken]);

  const fetchDocuments = useCallback(async (userId: string) => {
    const sessionId = generationResult?.sessionId || generationProgress?.sessionId;
    if (!sessionId) {
      throw new Error('No generation session available');
    }
    const token = await getAccessToken();
    return batchProfileApi.getUserDocuments(userId, sessionId, token || '');
  }, [getAccessToken, generationResult, generationProgress]);

  const pollGenerationProgress = useCallback(async (sessionId: string) => {
    try {
      const token = await getAccessToken();
      const response = await batchProfileApi.getBatchGenerationStatus(sessionId, token || '');

      if (response.success && response.data) {
        setGenerationProgress(response.data);

        if (response.data.completed + response.data.failed + response.data.skipped >= response.data.total) {
          setIsGenerating(false);
          const resultResponse = await batchProfileApi.getBatchGenerationResult(sessionId, token || '');
          if (resultResponse.success && resultResponse.data) {
            setGenerationResult(resultResponse.data);
          }
        } else {
          setTimeout(() => pollGenerationProgress(sessionId), 3000);
        }
      }
    } catch (error) {
      console.error('Error polling generation progress:', error);
      setError('Failed to get generation status');
      setIsGenerating(false);
    }
  }, [getAccessToken]);

  const handleRetryGeneration = useCallback(async (userId: string, options?: { force?: boolean }) => {
    const sessionId = generationProgress?.sessionId ?? generationResult?.sessionId;

    if (!sessionId) {
      setError('No generation session available to retry');
      return;
    }

    setRetryingUsers((prev) => (prev.includes(userId) ? prev : [...prev, userId]));

    try {
      setGenerationResult((prev) => {
        if (!prev) {
          return prev;
        }

        const nextCompleted = prev.completed.filter((entry) => entry.userId !== userId);
        const nextFailed = prev.failed.filter((entry) => entry.userId !== userId);
        const nextSkipped = prev.skipped.filter((entry) => entry.userId !== userId);

        return {
          ...prev,
          completed: nextCompleted,
          failed: nextFailed,
          skipped: nextSkipped,
          details: prev.details.filter((entry) => entry.userId !== userId),
          summary: {
            ...prev.summary,
            completed: nextCompleted.length,
            failed: nextFailed.length,
            skipped: nextSkipped.length
          }
        };
      });

      const token = await getAccessToken();
      await batchProfileApi.retryGeneration(
        sessionId,
        [userId],
        token || '',
        { forceRegeneration: options?.force ?? false }
      );

      setIsGenerating(true);
      void pollGenerationProgress(sessionId);
    } catch (err) {
      console.error('Error triggering regeneration:', err);
      const message = err instanceof Error ? err.message : 'Failed to trigger regeneration';
      setError(message);
    } finally {
      setRetryingUsers((prev) => prev.filter((id) => id !== userId));
    }
  }, [generationProgress?.sessionId, generationResult?.sessionId, getAccessToken, pollGenerationProgress]);

  const handleBulkPlaceholderRegeneration = useCallback(async () => {
    const sessionId = generationProgress?.sessionId ?? generationResult?.sessionId;

    if (!sessionId) {
      setError('No generation session available to retry');
      return;
    }

    setIsPlaceholderScanRunning(true);

    let placeholderIds: string[] = [];

    try {
      const token = await getAccessToken();
      const targetUserIds = new Set<string>();
      assignments.forEach((assignment) => {
        const userId = assignment.assignedUser?.userId;
        if (assignment.isAssigned && typeof userId === 'string' && userId.trim()) {
          targetUserIds.add(userId.trim());
        }
      });
      generationResult?.details.forEach((detail) => {
        if (typeof detail.userId === 'string' && detail.userId.trim()) {
          targetUserIds.add(detail.userId.trim());
        }
      });
      const candidateList = Array.from(targetUserIds);

      const detection = await batchProfileApi.detectPlaceholderProfiles(
        sessionId,
        token || '',
        candidateList.length > 0 ? candidateList : undefined
      );

      if (detection.errors && detection.errors.length > 0) {
        console.warn('Placeholder scan errors:', detection.errors);
      }

      placeholderIds = detection.placeholderUserIds ?? [];

      if (placeholderIds.length === 0) {
        if (detection.message) {
          setError(detection.message);
        } else if (detection.missingProfiles.length || detection.unknownUserIds.length) {
          setError('No placeholder profiles detected. Some profiles could not be checked (see console for details).');
          console.warn('Placeholder scan summary', detection);
        } else {
          setError('No placeholder profiles detected for regeneration');
        }
        return;
      }

      const placeholderSet = new Set(placeholderIds);

      setGenerationResult((prev) => {
        if (!prev) {
          return prev;
        }

        const nextCompleted = prev.completed.filter((entry) => !placeholderSet.has(entry.userId));
        const nextFailed = prev.failed.filter((entry) => !placeholderSet.has(entry.userId));
        const nextSkipped = prev.skipped.filter((entry) => !placeholderSet.has(entry.userId));

        return {
          ...prev,
          completed: nextCompleted,
          failed: nextFailed,
          skipped: nextSkipped,
          details: prev.details.filter((entry) => !placeholderSet.has(entry.userId)),
          summary: {
            ...prev.summary,
            completed: nextCompleted.length,
            failed: nextFailed.length,
            skipped: nextSkipped.length
          }
        };
      });

      setRetryingUsers((prev) => Array.from(new Set([...prev, ...placeholderIds])));

      await batchProfileApi.retryGeneration(
        sessionId,
        placeholderIds,
        token || '',
        { forceRegeneration: true }
      );

      setIsGenerating(true);
      void pollGenerationProgress(sessionId);
    } catch (err) {
      console.error('Error regenerating placeholder profiles:', err);
      const message = err instanceof Error ? err.message : 'Failed to regenerate placeholder profiles';
      setError(message);
    } finally {
      if (placeholderIds.length > 0) {
        const removalSet = new Set(placeholderIds);
        setRetryingUsers((prev) => prev.filter((id) => !removalSet.has(id)));
      }
      setIsPlaceholderScanRunning(false);
    }
  }, [assignments, generationProgress?.sessionId, generationResult?.sessionId, generationResult?.details, getAccessToken, pollGenerationProgress]);

  const pollScanProgress = useCallback(async (sessionId: string) => {
    try {
      const token = await getAccessToken();
      const response = await batchProfileApi.getScanStatus(sessionId, token || '');

      if (response.success && response.data) {
        const result = response.data;
        setScanResults(result);
        setScanCacheKey(prev => result.cacheKey || prev);

        const totalFolders = result.totalFolders || 0;
        const processedFolders = result.processedFolders || 0;
        const progressFromBackend = typeof result.progress === 'number' ? result.progress : undefined;
        const derivedProgress = totalFolders > 0 ? (processedFolders / totalFolders) * 100 : 0;
        const progress = progressFromBackend !== undefined ? progressFromBackend : derivedProgress;
        setScanProgress(progress);
        setCurrentScanPath(result.currentPath || '');

        if (result.scanCompleted) {
          setIsScanning(false);
        } else {
          setTimeout(() => pollScanProgress(sessionId), 2000);
        }
      }
    } catch (error) {
      console.error('Error polling scan progress:', error);
      setError('Failed to get scan status');
      setIsScanning(false);
    }
  }, [getAccessToken]);

  const computeAssignmentKey = useCallback((folder: SharePointFolder | undefined | null) => {
    if (!folder) {
      return null;
    }
    const id = folder.id || (folder as any).itemId;
    if (id) {
      return `id:${id}`;
    }
    const path = (
      folder.path ||
      (folder as any).folder_path ||
      (folder as any).folderPath ||
      ''
    )
      .trim()
      .toLowerCase();
    if (path) {
      return `path:${path}`;
    }
    const siteId = folder.siteId || (folder as any).site_id || '';
    const driveId = folder.driveId || (folder as any).drive_id || '';
    return `${siteId}|${driveId}` || null;
  }, []);

  const buildSuggestedUser = useCallback((matchEntry: UserMatch | undefined | null): AssignedUserSummary | null => {
    if (!matchEntry || matchEntry.isRejected) {
      return null;
    }

    if (matchEntry.user) {
      return {
        userId: matchEntry.user.id || matchEntry.user.mail || matchEntry.userName,
        displayName: matchEntry.user.displayName || matchEntry.userName,
        email: matchEntry.user.mail || matchEntry.userEmail,
        hasProfile: matchEntry.user.hasProfile ?? false
      };
    }

    if (matchEntry.userName) {
      return {
        userId: matchEntry.userEmail || matchEntry.id || matchEntry.folderPath || matchEntry.folderName,
        displayName: matchEntry.userName,
        email: matchEntry.userEmail,
        hasProfile: false
      };
    }

    return null;
  }, []);

  const applyAssignmentsFromMatches = useCallback((
    folders: SharePointFolder[],
    matchesSource: UserMatch[]
  ): FolderAssignment[] => {
    const matchesByKey = new Map<string, UserMatch>();
    matchesSource.forEach((matchEntry) => {
      const key = computeAssignmentKey(matchEntry.folder);
      if (key) {
        matchesByKey.set(key, matchEntry);
      }
    });

    return folders.map((folder) => {
      const key = computeAssignmentKey(folder);
      const matchEntry = key ? matchesByKey.get(key) : undefined;
      const suggestedUser = buildSuggestedUser(matchEntry);

      return {
        folder,
        assignedUser: undefined,
        suggestedUser: suggestedUser || undefined,
        matchConfidence: matchEntry?.confidence,
        matchMethod: matchEntry?.matchMethod,
        requiresReview: matchEntry?.requiresReview,
        matchNotes: matchEntry?.notes,
        matchId: matchEntry?.id,
        isAssigned: false
      };
    });
  }, [buildSuggestedUser, computeAssignmentKey]);

  const loadAssignments = useCallback(async (overrideMatches?: UserMatch[]) => {
    if (!scanCacheKey) {
      return;
    }

    try {
      setIsLoadingAssignments(true);
      setAssignmentError(null);

      const token = await getAccessToken();
      const response = await batchProfileApi.getAssignments(token || '', {
        scanSessionId: scanSessionId || undefined,
        cacheKey: scanCacheKey || undefined
      });

      if (response.success && response.data) {
        const sourceMatches = overrideMatches ?? matchesRef.current;
        const matchesByKey = new Map<string, UserMatch>();
        sourceMatches.forEach((matchEntry) => {
          const key = computeAssignmentKey(matchEntry.folder);
          if (key) {
            matchesByKey.set(key, matchEntry);
          }
        });
        const enhancedAssignments = response.data.folders.map((assignment) => {
          if (assignment.assignedUser) {
            return assignment;
          }

          const key = computeAssignmentKey(assignment.folder);
          const matchEntry = key ? matchesByKey.get(key) : undefined;
          const suggestedUser = buildSuggestedUser(matchEntry);

          if (!matchEntry || !suggestedUser) {
            return assignment;
          }

          return {
            ...assignment,
            suggestedUser,
            matchConfidence: matchEntry.confidence,
            matchMethod: matchEntry.matchMethod,
            requiresReview: matchEntry.requiresReview,
        matchNotes: matchEntry.notes,
        matchId: matchEntry.id,
        isAssigned: false
      };
    });

        setAssignments(enhancedAssignments);
        setAssignmentStats(computeAssignmentStats(enhancedAssignments));
        setActiveStep((prev) => (prev < 1 ? 1 : prev));
      } else {
        throw new Error(response.error || 'Failed to load folder assignments');
      }
    } catch (err) {
      console.error('Error loading assignments:', err);
      setAssignmentError(err instanceof Error ? err.message : 'Failed to load assignments');

      if (scanResults?.folders?.length) {
        const matchesSource = overrideMatches ?? matchesRef.current;
        if (matchesSource.length > 0) {
          const fallbackAssignments = applyAssignmentsFromMatches(scanResults.folders, matchesSource);
          if (fallbackAssignments.length) {
            setAssignments(fallbackAssignments);
            setAssignmentStats(computeAssignmentStats(fallbackAssignments));
            setActiveStep((prev) => (prev < 1 ? 1 : prev));
          }
        } else {
          const emptyAssignments = scanResults.folders.map(buildEmptyAssignment);
          setAssignments(emptyAssignments);
          setAssignmentStats(computeAssignmentStats(emptyAssignments));
        }
      }
    } finally {
      setIsLoadingAssignments(false);
    }
  }, [applyAssignmentsFromMatches, buildSuggestedUser, computeAssignmentKey, getAccessToken, scanCacheKey, scanSessionId, scanResults?.folders]);

  useEffect(() => {
    if (!scanSessionId || isMatching || matchesRef.current.length > 0) {
      return;
    }

    let cancelled = false;

    const pollPersistedMatches = async () => {
      let attempts = 0;
      while (!cancelled && matchesRef.current.length === 0 && attempts < 5) {
        attempts += 1;
        try {
          const token = await getAccessToken();
          const response = await batchProfileApi.getSessionMatches(token || '', scanSessionId);
          if (!cancelled && response.success && response.data && response.data.length > 0) {
            const normalized = response.data.map((match) => mapMatchFromApi(match));
            matchesRef.current = normalized;
            setMatches(normalized);
            await loadAssignments(normalized);
            return;
          }
        } catch (error) {
          if (!cancelled) {
            console.error('Failed to load persisted matches:', error);
          }
        }

        if (!cancelled && matchesRef.current.length === 0) {
          await new Promise((resolve) => setTimeout(resolve, 4000));
        }
      }
    };

    pollPersistedMatches();

    return () => {
      cancelled = true;
    };
  }, [scanSessionId, isMatching, getAccessToken, loadAssignments]);

  // Step 1: SharePoint Scanning
  const startScan = useCallback(async () => {
    try {
      setIsScanning(true);
      setError(null);
      setScanProgress(0);
      setActiveStep(0);
      setScanCacheKey(null);
      setAssignments([]);
      setAssignmentStats(computeAssignmentStats([]));
      setAssignmentError(null);
      setMatches([]);
      matchesRef.current = [];

      const token = await getAccessToken();
      const response = await batchProfileApi.startScan(scanConfig, token || '');

      if (response.success && response.data) {
        setScanSessionId(response.data.sessionId);
        setScanCacheKey(response.data.cacheKey);
        pollScanProgress(response.data.sessionId);
      } else {
        throw new Error(response.error || 'Failed to start scan');
      }
    } catch (error) {
      console.error('Error starting scan:', error);
      setError(error instanceof Error ? error.message : 'Failed to start scan');
      setIsScanning(false);
    }
  }, [scanConfig, getAccessToken, pollScanProgress]);

  // Step 2: User Matching (run automatically after scan)
  const runMatching = useCallback(async () => {
    if (!scanResults || !scanSessionId) {
      return;
    }

    try {
      setIsMatching(true);
      setError(null);
      setActiveStep(1);

      // Always load latest assignments so the table shows folders even if matching fails
      await loadAssignments();

      const token = await getAccessToken();

      const matchResponse = await batchProfileApi.matchUsersToFolders(
        scanResults.folders,
        {},
        token || '',
        { scanSessionId, cacheKey: scanCacheKey ?? undefined }
      );

      if (matchResponse.success && matchResponse.data) {
        const normalizedMatches = matchResponse.data.map((match) => mapMatchFromApi(match));
        matchesRef.current = normalizedMatches;
        setMatches(normalizedMatches);
        await loadAssignments(normalizedMatches);
      } else {
        throw new Error(matchResponse.error || 'Failed to match users to folders');
      }
    } catch (error) {
      console.error('Error running automatic matching:', error);
      const isTimeout = (error as AxiosError)?.code === 'ECONNABORTED';
      if (!isTimeout) {
        setError(error instanceof Error ? error.message : 'Failed to match users to folders');
      }
      const matchesSource = matchesRef.current.length > 0 ? matchesRef.current : [];
      await loadAssignments(matchesSource);

      if (scanResults?.folders?.length && matchesSource.length > 0) {
        const fallbackAssignments = applyAssignmentsFromMatches(scanResults.folders, matchesSource);
        if (fallbackAssignments.length) {
          setAssignments(fallbackAssignments);
          setAssignmentStats(computeAssignmentStats(fallbackAssignments));
          setActiveStep((prev) => (prev < 1 ? 1 : prev));
        }
      } else if (scanResults?.folders?.length) {
        const emptyAssignments = scanResults.folders.map(buildEmptyAssignment);
        setAssignments(emptyAssignments);
        setAssignmentStats(computeAssignmentStats(emptyAssignments));
        setActiveStep((prev) => (prev < 1 ? 1 : prev));
      }
    } finally {
      setIsMatching(false);
    }
  }, [scanResults, scanSessionId, scanCacheKey, getAccessToken, loadAssignments, applyAssignmentsFromMatches]);

  useEffect(() => {
    if (scanResults?.scanCompleted && scanCacheKey) {
      runMatching();
    }
  }, [scanResults?.scanCompleted, scanCacheKey, runMatching]);

  const applyMatchUpdate = useCallback((updatedMatch: UserMatch) => {
    setMatches((prev) => {
      const next = prev.map((match) => (match.id === updatedMatch.id ? updatedMatch : match));
      matchesRef.current = next;
      return next;
    });
  }, []);

  const handleDismissSuggestion = useCallback(async (assignment: FolderAssignment) => {
    const applyLocalRemoval = () => {
      setAssignments((prev) => {
        const updated = prev.map((entry) => (
          entry.folder.id === assignment.folder.id
            ? {
                ...entry,
                suggestedUser: undefined,
                matchConfidence: undefined,
                matchMethod: undefined,
                requiresReview: false,
                matchNotes: undefined,
                matchId: assignment.matchId,
                isAssigned: Boolean(entry.assignedUser)
              }
            : entry
        ));
        setAssignmentStats(computeAssignmentStats(updated));
        return updated;
      });
    };

    if (!assignment.matchId || !scanSessionId) {
      applyLocalRemoval();
      return;
    }

    try {
      setIsPersistingMatch(true);
      const token = await getAccessToken();
      const response = await batchProfileApi.updateMatch(
        assignment.matchId,
        {
          scanSessionId,
          isApproved: false,
          isRejected: true
        },
        token || ''
      );

      if (response.success && response.data) {
        const updated = mapMatchFromApi(response.data);
        applyMatchUpdate(updated);
        matchesRef.current = matchesRef.current.map((match) => (match.id === updated.id ? updated : match));
      }

      applyLocalRemoval();
    } catch (err) {
      console.error('Error dismissing suggestion:', err);
      setError(err instanceof Error ? err.message : 'Failed to dismiss suggestion');
      await loadAssignments(matchesRef.current);
    } finally {
      setIsPersistingMatch(false);
    }
  }, [scanSessionId, getAccessToken, applyMatchUpdate, loadAssignments]);

  // Match management
  const handleOpenAssignment = useCallback((assignment: FolderAssignment) => {
    setAssignmentDialog({
      open: true,
      folder: assignment.folder,
      matchId: assignment.matchId ?? null,
      currentUser: assignment.assignedUser ?? assignment.suggestedUser ?? null
    });
  }, []);

  const handleCloseAssignmentDialog = useCallback(() => {
    if (isSubmittingAssignment) {
      return;
    }
    setAssignmentDialog({ open: false, folder: null, matchId: null, currentUser: null });
  }, [isSubmittingAssignment]);

  const handleUserSelection = useCallback(async (users: BrowserUser[]) => {
    if (!assignmentDialog.open || !assignmentDialog.folder || !scanSessionId) {
      return;
    }

    if (!users || users.length === 0 || isSubmittingAssignment) {
      return;
    }

    const selectedUser = users[0];
    if (!selectedUser) {
      return;
    }

    if (
      assignmentDialog.currentUser &&
      assignmentDialog.currentUser.userId === selectedUser.id
    ) {
      setAssignmentDialog({ open: false, folder: null, matchId: null, currentUser: null });
      return;
    }

    const matchId = assignmentDialog.matchId;
    if (!matchId) {
      console.warn('Missing matchId for folder mapping, unable to persist selection.');
      return;
    }

    try {
      setIsSubmittingAssignment(true);
      setIsPersistingMatch(true);
      const token = await getAccessToken();

      const response = await batchProfileApi.updateMatch(
        matchId,
        {
          scanSessionId,
          userId: selectedUser.id,
          isApproved: true,
          isRejected: false,
          manuallyVerified: true
        },
        token || ''
      );

      if (response.success && response.data) {
        applyMatchUpdate(mapMatchFromApi(response.data));
      }

      await loadAssignments(matchesRef.current);
      setAssignmentDialog({ open: false, folder: null, matchId: null, currentUser: null });
    } catch (err) {
      console.error('Error mapping folder to user:', err);
      setAssignmentError(err instanceof Error ? err.message : 'Failed to map folder to user');
    } finally {
      setIsSubmittingAssignment(false);
      setIsPersistingMatch(false);
    }
  }, [assignmentDialog, getAccessToken, loadAssignments, isSubmittingAssignment, scanSessionId, applyMatchUpdate]);

  const handleUnassign = useCallback(async (assignment: FolderAssignment) => {
    if (!assignment.assignedUser) {
      return;
    }

    try {
      setIsSubmittingAssignment(true);
      const token = await getAccessToken();
      await batchProfileApi.unassignFolder(assignment.assignedUser.userId, token || '');
      await loadAssignments();
    } catch (err) {
      console.error('Error unassigning folder:', err);
      setAssignmentError(err instanceof Error ? err.message : 'Failed to remove folder assignment');
    } finally {
      setIsSubmittingAssignment(false);
    }
  }, [getAccessToken, loadAssignments]);

  const handleAssignAll = useCallback(async () => {
    const pending = assignments.filter((assignment) => !assignment.isAssigned && assignment.suggestedUser);
    if (pending.length === 0) {
      return;
    }

    setIsBulkAssigning(true);
    setAssignmentError(null);

    const errors: string[] = [];
    let token: string | null = null;
    let workingAssignments = assignments;

    const applyLocalUpdate = (updater: (prev: FolderAssignment[]) => FolderAssignment[]) => {
      workingAssignments = updater(workingAssignments);
      setAssignments(workingAssignments);
      setAssignmentStats(computeAssignmentStats(workingAssignments));
    };

    try {
      token = await getAccessToken();

      for (const assignment of pending) {
        const suggested = assignment.suggestedUser;
        if (!suggested) {
          continue;
        }

        try {
          await batchProfileApi.assignFolder(
            {
              userId: suggested.userId,
              folderPath: assignment.folder.path,
              folderName: assignment.folder.name,
              siteId: assignment.folder.siteId || assignment.folder.site_id,
              siteKey: assignment.folder.siteKey || assignment.folder.site_key,
              driveId: assignment.folder.driveId || assignment.folder.drive_id,
              webUrl: assignment.folder.webUrl
            },
            token || ''
          );

          const assignmentKey = computeAssignmentKey(assignment.folder);
          applyLocalUpdate((prev) => prev.map((entry) => {
            if (computeAssignmentKey(entry.folder) !== assignmentKey) {
              return entry;
            }

            return {
              ...entry,
              assignedUser: {
                userId: suggested.userId,
                displayName: suggested.displayName,
                email: suggested.email,
                hasProfile: suggested.hasProfile
              },
              suggestedUser: undefined,
              matchConfidence: undefined,
              matchMethod: undefined,
              requiresReview: false,
              matchNotes: undefined,
              isAssigned: true,
              matchId: assignment.matchId
            };
          }));

          if (assignment.matchId) {
            matchesRef.current = matchesRef.current.map((match) => (
              match.id === assignment.matchId
                ? { ...match, isApproved: true, isRejected: false }
                : match
            ));
            setMatches([...matchesRef.current]);
          }
        } catch (assignError) {
          const message = assignError instanceof Error ? assignError.message : 'Unknown error';
          errors.push(`${assignment.folder.name}: ${message}`);
        }
      }

      if (errors.length > 0) {
        setAssignmentError(`Some folders could not be assigned:\n${errors.join('\n')}`);
      }

      await loadAssignments();
    } catch (err) {
      console.error('Error assigning folders:', err);
      const message = err instanceof Error ? err.message : 'Failed to assign folders';
      setAssignmentError(message);
    } finally {
      setIsBulkAssigning(false);
    }
  }, [assignments, computeAssignmentKey, getAccessToken, loadAssignments]);

  // Step 3: Batch Generation
  const startBatchGeneration = useCallback(async () => {
    const payload = buildGenerationStartPayload();

    if (payload.matches.length === 0) {
      setError('No assigned folders available for generation');
      return;
    }

    try {
      setIsGenerating(true);
      setError(null);
      setActiveStep(2);

      const token = await getAccessToken();
      const response = await batchProfileApi.startBatchGeneration(
        payload,
        token || ''
      );

      if (response.success && response.data) {
        pollGenerationProgress(response.data.sessionId);
      } else {
        throw new Error(response.error || 'Failed to start batch generation');
      }
    } catch (error) {
      console.error('Error starting batch generation:', error);
      setError(error instanceof Error ? error.message : 'Failed to start batch generation');
      setIsGenerating(false);
    }
  }, [buildGenerationStartPayload, getAccessToken, pollGenerationProgress]);

  // Computed values
  const scanComplete = scanResults !== null && scanResults.scanCompleted === true;
  const approvedCount = assignments.filter((assignment) => assignment.isAssigned).length;

  // Render the appropriate view based on the active step
  const renderMainContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <ScannerStatus
            isScanning={isScanning}
            scanProgress={scanProgress}
            scanResults={scanResults}
            currentPath={currentScanPath}
            error={error || undefined}
            onCancel={async () => {
              if (scanSessionId) {
                try {
                  const token = await getAccessToken();
                  await batchProfileApi.cancelScan(scanSessionId, token || '');
                  setIsScanning(false);
                } catch (error) {
                  console.error('Error cancelling scan:', error);
                }
              }
            }}
          />
        );

      case 1:
        return (
          <Box display="flex" flexDirection="column" gap={3}>
            <FolderAssignmentsView
              assignments={assignments}
              isLoading={isLoadingAssignments}
              stats={assignmentStats}
              onAssign={handleOpenAssignment}
              onUnassign={handleUnassign}
              onRejectSuggestion={handleDismissSuggestion}
              onAssignAll={handleAssignAll}
              isSubmitting={isSubmittingAssignment}
              isBulkAssigning={isBulkAssigning}
              errorMessage={assignmentError}
            />

          </Box>
        );

      case 2:
        return (
          <GenerationProgressView
            progress={generationProgress}
            result={generationResult}
            isGenerating={isGenerating}
            assignments={assignments}
            onFetchProfile={fetchGeneratedProfile}
            onFetchDocuments={fetchDocuments}
            onRetry={handleRetryGeneration}
            retryingUserIds={retryingUsers}
            onBulkRegeneratePlaceholders={handleBulkPlaceholderRegeneration}
            isBulkRegenerating={isPlaceholderScanRunning}
            onExport={async () => {
              // TODO: Implement export logic
              console.log('Export results');
            }}
          />
        );

      default:
        return null;
    }
  };

  return (
    <Box className="batch-profiles-container" sx={{ display: 'flex', height: '100vh' }}>
      <SharedSidebar isOpen={sidebarOpen} onToggle={() => setSidebarOpen(!sidebarOpen)}>
        <ConfigurationPanel
          scanConfig={scanConfig}
          onScanConfigChange={setScanConfig}
          generationOptions={generationOptions}
          onGenerationOptionsChange={setGenerationOptions}
          onStartScan={startScan}
          onStartGeneration={startBatchGeneration}
          isScanning={isScanning}
          isMatching={isMatching || isPersistingMatch}
          isGenerating={isGenerating}
          scanComplete={scanComplete}
          approvedCount={approvedCount}
        />
      </SharedSidebar>

      <CenterWorkspace>
        <Container maxWidth="xl" sx={{ py: 4 }}>
          <Typography variant="h4" gutterBottom>
            Batch Profile Creation
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            Create professional profiles in bulk from SharePoint materials
          </Typography>

          {isMatching && (
            <Alert severity="info" sx={{ mb: 3 }}>
              Matching SharePoint folders to Entra users… this can take up to a couple of minutes.
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Paper elevation={1} sx={{ p: 3 }}>
            {renderMainContent()}
          </Paper>

          <Dialog
            open={assignmentDialog.open}
            onClose={handleCloseAssignmentDialog}
            fullWidth
            maxWidth="md"
          >
            <DialogTitle>Assign SharePoint Folder</DialogTitle>
            <DialogContent dividers>
              <Typography variant="subtitle1" gutterBottom>
                {assignmentDialog.folder?.name}
              </Typography>
              {assignmentDialog.folder?.path && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {assignmentDialog.folder.path}
                </Typography>
              )}

              <UserBrowser
                multiSelect={false}
                maxSelections={1}
                onSelectionChange={handleUserSelection}
                filters={{ hasProfile: { value: false, visible: true, locked: false } }}
              />

              {isSubmittingAssignment && (
                <Box display="flex" justifyContent="center" mt={3}>
                  <CircularProgress size={24} />
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseAssignmentDialog} disabled={isSubmittingAssignment}>
                Close
              </Button>
            </DialogActions>
          </Dialog>
        </Container>
      </CenterWorkspace>
    </Box>
  );
};

export default BatchProfiles;
